'use client';

import { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { getTransactionFee } from '@peach/types/payments/fees.js';
import { tw } from '@tw';
import { createDonationCheckout } from '@/app/actions/donations';
import { formatPrice } from '@/lib/utils/format';
import { callAction } from '@/lib/call-action';

const EmbeddedCheckoutPanel = dynamic(() => import('@/components/payment/EmbeddedCheckoutPanel'), {
  ssr: false,
});

const TIERS = [5, 10, 50];

interface TipCardProps {
  tutorUsername: string;
  tutorName: string;
}

/**
 * Never renders nothing: the rating that used to gate the tip is gone, and a
 * tip does not retire the card either — a donor may give any number of times.
 */
export function TipCard({ tutorUsername, tutorName }: TipCardProps) {
  const [amount, setAmount] = useState(TIERS[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thanked, setThanked] = useState(false);
  const [embedded, setEmbedded] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);

  const { serviceFee, transactionFee } = getTransactionFee(amount);
  const total = amount + serviceFee + transactionFee;

  const start = useCallback(
    async (tip: number) => {
      setBusy(true);
      setError(null);
      posthog.capture('donation_checkout_started', {
        provider_username: tutorUsername,
        creator_username: tutorUsername,
        tutor_username: tutorUsername,
        amount: tip,
        surface: 'storefront_tip',
      });
      const result = await callAction(() => createDonationCheckout(tutorUsername, tip));
      setBusy(false);
      if (!result.success) {
        setError('Could not open the payment — try again');
        return;
      }
      const { url, clientSecret, publishableKey } = result.data;
      if (clientSecret && publishableKey) {
        setEmbedded({ clientSecret, publishableKey });
        return;
      }
      if (url) {
        window.location.assign(url);
        return;
      }
      Sentry.captureMessage('Donation checkout came back empty', {
        level: 'error',
        extra: { tutorUsername, amount: tip },
      });
      setError('Could not open the payment — try again');
    },
    [tutorUsername]
  );

  const picked = TIERS.indexOf(amount);

  return (
    <div className="rounded-[var(--sf-radius)] bg-[var(--sf-card)] px-3.5 py-3">
      <span className="block sf-title text-title-2 font-extrabold text-[var(--sf-ink)]">
        {thanked ? `${tutorName} has it — all of it.` : `Tip ${tutorName} after a call`}
      </span>

      {!thanked && !embedded && (
        <>
          {/* One track and one sliding fill rather than three buttons that each
              light up: the amounts are a single choice. */}
          <div
            role="group"
            aria-label="Tip amount"
            className="relative mt-2.5 flex rounded-full bg-[var(--sf-page)] p-1"
          >
            <span
              aria-hidden
              style={{ transform: `translateX(${picked * 100}%)` }}
              className="absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/3)] rounded-full bg-[var(--sf-ink)] transition-transform duration-200 ease-out motion-reduce:transition-none"
            />
            {TIERS.map((tier) => (
              <button
                key={tier}
                type="button"
                onClick={() => setAmount(tier)}
                aria-pressed={amount === tier}
                className={tw(
                  'mm-btn relative flex-1 rounded-full py-2 sf-num text-p-3 font-extrabold transition-colors',
                  amount === tier ? 'text-[var(--sf-page)]' : 'text-[var(--sf-ink)]'
                )}
              >
                ${tier}
              </button>
            ))}
          </div>

          <span className="sf-note mt-2 block">
            {tutorName} keeps {formatPrice({ amount, currency: 'USD' })} — you pay{' '}
            {formatPrice({ amount: total, currency: 'USD' })} with fees.
          </span>

          {error && <span className="sf-note mt-1 block text-error">{error}</span>}

          <button
            type="button"
            disabled={busy}
            onClick={() => void start(amount)}
            className="mm-btn sf-cta sf-cta-ink mt-2"
          >
            {busy ? 'Opening…' : `Send ${formatPrice({ amount, currency: 'USD' })}`}
          </button>
        </>
      )}

      {embedded && (
        <div className="mt-2.5 overflow-hidden rounded-[10px]">
          <EmbeddedCheckoutPanel
            clientSecret={embedded.clientSecret}
            publishableKey={embedded.publishableKey}
            onComplete={() => {
              setEmbedded(null);
              setThanked(true);
              posthog.capture('donation_checkout_completed', {
                provider_username: tutorUsername,
                creator_username: tutorUsername,
                tutor_username: tutorUsername,
                amount,
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
